import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { dmY } from "@/lib/dateFmt";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChartLineUp, Percent, ArrowLeft, CurrencyInr, ArrowsCounterClockwise } from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmt2 = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

/**
 * Admin Commission Report — dedicated drill-in from the Dashboard
 * "Commission · Last 3 Months" tile (v1.20.7).
 *
 * Layout:
 *   - 3 KPI cards: total commission, portal fees, combined
 *   - Monthly breakdown bar list
 *   - Party-wise breakdown table sorted by total descending
 *   - Latest 200 commission/portal_fee transaction rows
 *
 * Backend: GET /api/reports/commission?months=N | date_from=&date_to=
 */
export default function CommissionReport() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [data, setData] = useState(null);

    const load = async (opts = {}) => {
        setLoading(true);
        try {
            const params = {};
            if (opts.months) params.months = opts.months;
            else if (dateFrom || dateTo) {
                if (dateFrom) params.date_from = dateFrom;
                if (dateTo) params.date_to = dateTo;
            } else params.months = 3;
            const { data } = await api.get("/reports/commission", { params });
            // v1.20.16 — if the default 3-month window is empty, auto-widen
            // to 12 months so the dashboard drill-in never lands on a blank
            // page for accounts whose last commission was months ago.
            if (!opts.months && !dateFrom && !dateTo && (!data.rows || data.rows.length === 0)) {
                const { data: wider } = await api.get("/reports/commission", { params: { months: 12 } });
                setData(wider);
                if (wider.date_from) setDateFrom(wider.date_from);
                if (wider.date_to) setDateTo(wider.date_to);
                if (wider.totals?.count > 0) toast.info("No commission in the last 3 months — showing last 12 months");
                return;
            }
            setData(data);
            if (!dateFrom && data.date_from) setDateFrom(data.date_from);
            if (!dateTo && data.date_to) setDateTo(data.date_to);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    // Trigger initial load + on explicit "Apply" clicks
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const applyPreset = (months) => {
        const now = new Date();
        const from = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
        setDateFrom(from.toISOString().slice(0, 10));
        setDateTo(now.toISOString().slice(0, 10));
        setTimeout(load, 0);
    };

    const monthly = data?.monthly || [];
    const byParty = data?.by_party || [];
    const pivot = data?.by_party_by_month || [];
    const rows = data?.rows || [];
    const totals = data?.totals || { commission: 0, portal_fee: 0, total: 0, count: 0 };
    const monthlyMax = useMemo(() => Math.max(1, ...monthly.map((m) => m.amount || 0)), [monthly]);
    // v1.25.42 — party_id filter (drill-into-single-party) works with the
    // existing date range so admin can inspect one party's commission history.
    const [partyFilter, setPartyFilter] = useState(null); // { id, name }
    const focusParty = async (pid, pname) => {
        setPartyFilter({ id: pid, name: pname });
        setLoading(true);
        try {
            const params = { party_id: pid };
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const { data: filtered } = await api.get("/reports/commission", { params });
            setData(filtered);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    const clearPartyFilter = () => { setPartyFilter(null); load(); };

    return (
        <div className="space-y-6" data-testid="commission-report-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                        <ArrowLeft size={12} /> Back to Dashboard
                    </Link>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900">Commission Report</h1>
                    <p className="mt-1 text-sm text-slate-600">Commission + portal fees earned in the selected range.</p>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">From</Label>
                        <Input type="date" data-testid="commission-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 h-9 text-sm" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">To</Label>
                        <Input type="date" data-testid="commission-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 h-9 text-sm" />
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        data-testid="commission-apply"
                        className="inline-flex h-9 items-center gap-1.5 rounded-md bg-blue-600 px-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        <ArrowsCounterClockwise size={13} className={loading ? "animate-spin" : ""} /> Apply
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                {[1, 3, 6, 12].map((m) => (
                    <button
                        key={m}
                        type="button"
                        onClick={() => applyPreset(m)}
                        data-testid={`commission-preset-${m}m`}
                        className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                    >
                        Last {m} {m === 1 ? "month" : "months"}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Kpi label="Commission" value={fmt(totals.commission)} icon={ChartLineUp} tone="emerald" testId="kpi-commission" />
                <Kpi label="Portal Fees" value={fmt(totals.portal_fee)} icon={Percent} tone="amber" testId="kpi-portal-fee" />
                <Kpi label="Combined Total" value={fmt(totals.total)} icon={CurrencyInr} tone="blue" testId="kpi-combined" accent />
            </div>

            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                    <div className="font-display text-sm font-semibold text-slate-900">Monthly breakdown</div>
                    <div className="text-[11px] text-slate-500">{monthly.length} months</div>
                </div>
                {monthly.length === 0 ? (
                    <div className="py-6 text-center text-sm text-slate-400">No commission earned in this range.</div>
                ) : (
                    <ul className="space-y-2" data-testid="commission-monthly-list">
                        {monthly.map((m) => {
                            const pct = Math.round(((m.amount || 0) / monthlyMax) * 100);
                            return (
                                <li key={m.month_key} className="flex items-center gap-3" data-testid={`commission-month-${m.month_key}`}>
                                    <div className="w-24 shrink-0 text-xs font-medium text-slate-700">{m.label}</div>
                                    <div className="relative h-6 flex-1 overflow-hidden rounded-md bg-slate-100">
                                        <div className="absolute inset-y-0 left-0 rounded-md bg-gradient-to-r from-emerald-500 to-teal-500" style={{ width: `${pct}%` }} />
                                    </div>
                                    <div className="w-32 shrink-0 text-right text-xs">
                                        <span className="font-mono font-semibold text-slate-900">{fmt(m.amount)}</span>
                                        <span className="ml-1 text-slate-500">· {m.count}</span>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                    <div className="font-display text-sm font-semibold text-slate-900">Party-wise breakdown</div>
                    <div className="text-[11px] text-slate-500">{byParty.length} parties</div>
                </div>
                {byParty.length === 0 ? (
                    <div className="p-6"><EmptyState icon={ChartLineUp} title="Nothing yet" description="No commission has been posted in this range." /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm" data-testid="commission-by-party-table">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-4 py-2 text-left">Party</th>
                                    <th className="px-3 py-2 text-right">Commission</th>
                                    <th className="px-3 py-2 text-right">Portal Fee</th>
                                    <th className="px-3 py-2 text-right">Total</th>
                                    <th className="px-3 py-2 text-right">Entries</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {byParty.map((r) => (
                                    <tr key={r.party_id} className="hover:bg-slate-50" data-testid={`commission-party-${r.party_id}`}>
                                        <td className="px-4 py-2 font-medium text-slate-900">{r.party_name}</td>
                                        <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-800">{fmt(r.commission)}</td>
                                        <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-800">{fmt(r.portal_fee)}</td>
                                        <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-slate-900">{fmt(r.total)}</td>
                                        <td className="px-3 py-2 text-right font-mono text-slate-600">{r.count}</td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <button
                                                type="button"
                                                onClick={() => focusParty(r.party_id, r.party_name)}
                                                className="mr-3 text-xs font-medium text-emerald-700 hover:underline"
                                                data-testid={`commission-focus-party-${r.party_id}`}
                                                title="See this party's commission month-by-month"
                                            >
                                                Commission by month →
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/admin/reports?party_id=${r.party_id}`)}
                                                className="text-xs font-medium text-blue-600 hover:underline"
                                                data-testid={`commission-open-party-${r.party_id}`}
                                            >
                                                Open party ledger →
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                <tr className="border-t-2 border-slate-200 bg-slate-50">
                                    <td className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Totals</td>
                                    <td className="px-3 py-2 text-right font-mono font-bold tabular-nums text-emerald-900">{fmt(totals.commission)}</td>
                                    <td className="px-3 py-2 text-right font-mono font-bold tabular-nums text-amber-900">{fmt(totals.portal_fee)}</td>
                                    <td className="px-3 py-2 text-right font-mono font-bold tabular-nums text-slate-900">{fmt(totals.total)}</td>
                                    <td className="px-3 py-2 text-right font-mono font-bold text-slate-700">{totals.count}</td>
                                    <td />
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                    <div>
                        <div className="font-display text-sm font-semibold text-slate-900">Party × Month pivot</div>
                        <div className="text-[10px] text-slate-500">Commission earned per party per month — click a party name to drill in</div>
                    </div>
                    {partyFilter && (
                        <button
                            type="button"
                            onClick={clearPartyFilter}
                            data-testid="commission-clear-party"
                            className="rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-200"
                        >
                            Focused on: {partyFilter.name} — Clear ✕
                        </button>
                    )}
                </div>
                {pivot.length === 0 || monthly.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-400">No commission earned in this range.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm" data-testid="commission-pivot-table">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="sticky left-0 z-10 bg-slate-50 px-4 py-2 text-left">Party</th>
                                    {monthly.map((m) => (
                                        <th key={m.month_key} className="px-3 py-2 text-right">{m.label}</th>
                                    ))}
                                    <th className="px-3 py-2 text-right border-l border-slate-200">Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {pivot.map((p) => (
                                    <tr key={p.party_id} className="hover:bg-emerald-50/30" data-testid={`commission-pivot-${p.party_id}`}>
                                        <td className="sticky left-0 z-10 bg-white px-4 py-2 font-medium text-slate-900 hover:bg-emerald-50/60">
                                            <button type="button" onClick={() => focusParty(p.party_id, p.party_name)} className="hover:text-emerald-700 hover:underline">
                                                {p.party_name}
                                            </button>
                                        </td>
                                        {monthly.map((m) => {
                                            const cell = p.months?.[m.month_key];
                                            return (
                                                <td key={m.month_key} className={`px-3 py-2 text-right font-mono tabular-nums ${cell ? "text-emerald-800" : "text-slate-300"}`}>
                                                    {cell ? fmt(cell.total) : "—"}
                                                </td>
                                            );
                                        })}
                                        <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-slate-900 border-l border-slate-200">{fmt(p.total)}</td>
                                    </tr>
                                ))}
                                <tr className="border-t-2 border-slate-200 bg-slate-50">
                                    <td className="sticky left-0 z-10 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Month totals</td>
                                    {monthly.map((m) => (
                                        <td key={m.month_key} className="px-3 py-2 text-right font-mono font-bold tabular-nums text-emerald-900">
                                            {fmt(m.amount)}
                                        </td>
                                    ))}
                                    <td className="px-3 py-2 text-right font-mono font-bold tabular-nums text-slate-900 border-l border-slate-200">{fmt(totals.total)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                    <div className="font-display text-sm font-semibold text-slate-900">Recent entries</div>
                    <div className="text-[11px] text-slate-500">showing latest {rows.length}</div>
                </div>
                {rows.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-400">No transactions in this range.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm" data-testid="commission-rows-table">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-4 py-2 text-left">Date</th>
                                    <th className="px-4 py-2 text-left">Party</th>
                                    <th className="px-3 py-2 text-left">Type</th>
                                    <th className="px-3 py-2 text-right">%</th>
                                    <th className="px-3 py-2 text-right">Amount</th>
                                    <th className="px-4 py-2 text-left">Description</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((r) => (
                                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`commission-row-${r.id}`}>
                                        <td className="px-4 py-2 whitespace-nowrap font-mono text-xs text-slate-700">{dmY(r.date)}</td>
                                        <td className="px-4 py-2 text-slate-900">{r.party_name}</td>
                                        <td className="px-3 py-2">
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${r.txn_type === "commission" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                                                {r.txn_type === "portal_fee" ? "Portal Fee" : "Commission"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-slate-600">{r.charge_percent ? `${Number(r.charge_percent).toFixed(2)}%` : ""}</td>
                                        <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-slate-900">{fmt2(r.amount)}</td>
                                        <td className="px-4 py-2 text-xs text-slate-600">{r.description}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

function Kpi({ label, value, icon: Icon, tone = "slate", accent, testId }) {
    const toneCls = {
        emerald: "from-emerald-500 to-teal-600",
        amber: "from-amber-500 to-orange-500",
        blue: "from-blue-500 to-indigo-600",
        slate: "from-slate-500 to-slate-700",
    }[tone] || "from-slate-500 to-slate-700";
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testId}>
            <div className="flex items-start justify-between gap-2">
                <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500">{label}</div>
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${toneCls} text-white shadow-sm`}>
                    <Icon size={16} weight="bold" />
                </div>
            </div>
            <div className={`mt-3 font-display text-2xl font-bold tabular-nums ${accent ? "text-blue-800" : "text-slate-900"}`}>
                {value}
            </div>
        </div>
    );
}
